import React, { useMemo, useState } from "react";
import {
  Building2,
  ClipboardCheck,
  FileText,
  Mail,
  MailCheck,
  Plus,
  X,
} from "lucide-react";

import TitleButtons from "../../../../components/TitleButtons";
import { useTheme } from "../../../../context/ThemeContext";

type SectionKey =
  | "visao-geral"
  | "com-pendencias"
  | "regras-ativas"
  | "aguardando-revisao"
  | "sem-regra";

type StatusRegraCliente =
  | "ativa"
  | "pendente_revisao"
  | "incompleta"
  | "inativa"
  | "sem_regra";

type TipoReferenciaComercial =
  | "sc"
  | "pi"
  | "po"
  | "pedido_compra"
  | "ordem_servico_cliente"
  | "contrato"
  | "nao_exige";

type PoliticaAgrupamentoNota =
  | "varios_servicos_mesma_nota"
  | "uma_nota_por_servico"
  | "uma_nota_por_pi_po"
  | "uma_nota_por_embarcacao"
  | "avaliar_caso_a_caso";

type DocumentoExigidoEnvio = "pdf" | "xml" | "pdf_xml" | "danfse" | "outro";

type CanalEnvioCliente = "email" | "portal" | "whatsapp" | "outro";

type TipoContatoFiscal =
  | "fiscal_cliente"
  | "solicitante"
  | "compras_cliente"
  | "financeiro_cliente"
  | "portal_cliente"
  | "outro";

type StatusItemRegra = "obrigatorio" | "opcional" | "nao_aplicavel";

type StatusValidacaoRegra =
  | "validada"
  | "pendente_validacao"
  | "desatualizada"
  | "sem_validacao";

type ContatoEnvioFiscal = {
  id: number;
  tipo: TipoContatoFiscal;
  nome: string;
  email: string | null;
  telefone: string | null;
  canal: CanalEnvioCliente;
  documento: DocumentoExigidoEnvio;
  observacao: string;
  status: StatusItemRegra;
};

type EtapaFluxoFiscal = {
  id: number;
  ordem: number;
  titulo: string;
  descricao: string;
  obrigatoria: boolean;
};

type RegraFiscalCliente = {
  id: number;
  clienteId: number;
  cliente: string;
  cnpjCpfCliente: string;
  grupoCliente: string;
  statusRegra: StatusRegraCliente;
  statusValidacao: StatusValidacaoRegra;
  responsavelInterno: string;
  revisadoEm: string | null;
  proximaRevisao: string | null;
  exigeScAntesEmissao: boolean;
  exigePiPoAntesEmissao: boolean;
  tipoReferenciaPrincipal: TipoReferenciaComercial;
  labelReferenciaPrincipal: string;
  referenciaObrigatoriaNaNota: boolean;
  textoReferenciaNaNota: string;
  politicaAgrupamentoNota: PoliticaAgrupamentoNota;
  permiteMultiplosServicosNaMesmaNota: boolean;
  exigeDescricaoIndividualPorServico: boolean;
  exigeAnexoRelatorioTecnico: boolean;
  exigeAnexoPedidoCompra: boolean;
  exigeLancamentoPortal: boolean;
  portalNome: string | null;
  portalUrl: string | null;
  prazoEnvioAposEmissaoHoras: number;
  observacoesEmissao: string;
  observacoesEnvio: string;
  contatosEnvio: ContatoEnvioFiscal[];
  fluxo: EtapaFluxoFiscal[];
};

type RegrasFiscaisClientesLayoutProps = {
  section?: SectionKey;
};

const DEPARTMENT_COLOR = "#2f3f59";

const fixedRegras: RegraFiscalCliente[] = [
  {
    id: 1,
    clienteId: 101,
    cliente: "Bravante Navegação S.A.",
    cnpjCpfCliente: "33.333.333/0001-33",
    grupoCliente: "Bravante / Marlin",
    statusRegra: "ativa",
    statusValidacao: "validada",
    responsavelInterno: "Financeiro",
    revisadoEm: "2026-05-08",
    proximaRevisao: "2026-08-08",
    exigeScAntesEmissao: true,
    exigePiPoAntesEmissao: true,
    tipoReferenciaPrincipal: "pi",
    labelReferenciaPrincipal: "PI/PO",
    referenciaObrigatoriaNaNota: true,
    textoReferenciaNaNota:
      "Mencionar o número do PI/PO nos dados complementares da NFS-e.",
    politicaAgrupamentoNota: "varios_servicos_mesma_nota",
    permiteMultiplosServicosNaMesmaNota: true,
    exigeDescricaoIndividualPorServico: true,
    exigeAnexoRelatorioTecnico: true,
    exigeAnexoPedidoCompra: true,
    exigeLancamentoPortal: true,
    portalNome: "Portal de Fornecedores Bravante",
    portalUrl: "https://portal.bravante.com.br",
    prazoEnvioAposEmissaoHoras: 24,
    observacoesEmissao:
      "Aguardar o recebimento da SC. Após a aprovação, aguardar o número de PI/PO. O PI/PO deve constar nos dados complementares da nota fiscal.",
    observacoesEnvio:
      "Após emitir a nota, enviar XML ao fiscal do cliente e PDF/DANFSe ao solicitante do serviço. Quando aplicável, anexar também no portal.",
    contatosEnvio: [
      {
        id: 1,
        tipo: "fiscal_cliente",
        nome: "Departamento Fiscal Bravante",
        email: "fiscal@bravante.com.br",
        telefone: null,
        canal: "email",
        documento: "xml",
        observacao: "Enviar XML da NFS-e após emissão.",
        status: "obrigatorio",
      },
      {
        id: 2,
        tipo: "solicitante",
        nome: "Solicitante do serviço",
        email: "solicitante@bravante.com.br",
        telefone: null,
        canal: "email",
        documento: "danfse",
        observacao: "Enviar PDF/DANFSe para quem solicitou o serviço.",
        status: "obrigatorio",
      },
      {
        id: 3,
        tipo: "portal_cliente",
        nome: "Portal Bravante",
        email: null,
        telefone: null,
        canal: "portal",
        documento: "pdf_xml",
        observacao: "Anexar PDF e XML quando o processo exigir portal.",
        status: "obrigatorio",
      },
    ],
    fluxo: [
      {
        id: 1,
        ordem: 1,
        titulo: "Aguardar SC",
        descricao: "Não emitir nota antes do recebimento da SC do cliente.",
        obrigatoria: true,
      },
      {
        id: 2,
        ordem: 2,
        titulo: "Aguardar PI/PO",
        descricao:
          "Após a SC, aguardar o número de PI/PO que será mencionado na nota.",
        obrigatoria: true,
      },
      {
        id: 3,
        ordem: 3,
        titulo: "Emitir NFS-e",
        descricao:
          "Inserir o PI/PO nos dados complementares e discriminar os serviços executados.",
        obrigatoria: true,
      },
      {
        id: 4,
        ordem: 4,
        titulo: "Enviar documentos",
        descricao:
          "Enviar XML ao fiscal, DANFSe ao solicitante e anexar documentos no portal.",
        obrigatoria: true,
      },
    ],
  },
  {
    id: 2,
    clienteId: 102,
    cliente: "Eustor Serviços Offshore Ltda",
    cnpjCpfCliente: "22.222.222/0001-22",
    grupoCliente: "Eustor",
    statusRegra: "ativa",
    statusValidacao: "validada",
    responsavelInterno: "Financeiro",
    revisadoEm: "2026-05-07",
    proximaRevisao: "2026-08-07",
    exigeScAntesEmissao: false,
    exigePiPoAntesEmissao: true,
    tipoReferenciaPrincipal: "po",
    labelReferenciaPrincipal: "PO",
    referenciaObrigatoriaNaNota: true,
    textoReferenciaNaNota:
      "Mencionar o número do PO nos dados complementares de cada NFS-e.",
    politicaAgrupamentoNota: "uma_nota_por_servico",
    permiteMultiplosServicosNaMesmaNota: false,
    exigeDescricaoIndividualPorServico: true,
    exigeAnexoRelatorioTecnico: true,
    exigeAnexoPedidoCompra: true,
    exigeLancamentoPortal: false,
    portalNome: null,
    portalUrl: null,
    prazoEnvioAposEmissaoHoras: 24,
    observacoesEmissao:
      "Mesmo que o PO seja único, emitir uma NFS-e por serviço realizado. Não agrupar quatro serviços em uma única nota.",
    observacoesEnvio:
      "Enviar XML e DANFSe ao fiscal. Encaminhar cópia ao solicitante quando indicado no pedido.",
    contatosEnvio: [
      {
        id: 1,
        tipo: "fiscal_cliente",
        nome: "Fiscal Eustor",
        email: "fiscal@eustor.com",
        telefone: null,
        canal: "email",
        documento: "pdf_xml",
        observacao: "Enviar XML e DANFSe de cada nota emitida.",
        status: "obrigatorio",
      },
      {
        id: 2,
        tipo: "solicitante",
        nome: "Solicitante do serviço",
        email: "operacoes@eustor.com",
        telefone: null,
        canal: "email",
        documento: "danfse",
        observacao: "Enviar DANFSe para conferência operacional.",
        status: "opcional",
      },
    ],
    fluxo: [
      {
        id: 1,
        ordem: 1,
        titulo: "Receber PO",
        descricao:
          "Confirmar número de PO antes de emitir qualquer nota fiscal.",
        obrigatoria: true,
      },
      {
        id: 2,
        ordem: 2,
        titulo: "Separar serviços",
        descricao:
          "Quando houver múltiplos serviços, emitir uma nota fiscal para cada serviço.",
        obrigatoria: true,
      },
      {
        id: 3,
        ordem: 3,
        titulo: "Emitir NFS-e",
        descricao:
          "Cada nota deve mencionar o mesmo PO quando os serviços estiverem vinculados ao mesmo pedido.",
        obrigatoria: true,
      },
      {
        id: 4,
        ordem: 4,
        titulo: "Enviar ao fiscal",
        descricao:
          "Enviar XML e DANFSe da nota para o e-mail fiscal cadastrado.",
        obrigatoria: true,
      },
    ],
  },
  {
    id: 3,
    clienteId: 103,
    cliente: "Googlemarine Serviços Marítimos Ltda",
    cnpjCpfCliente: "12.345.678/0001-90",
    grupoCliente: "Googlemarine",
    statusRegra: "ativa",
    statusValidacao: "pendente_validacao",
    responsavelInterno: "Guilherme Brito",
    revisadoEm: "2026-05-02",
    proximaRevisao: "2026-06-02",
    exigeScAntesEmissao: false,
    exigePiPoAntesEmissao: false,
    tipoReferenciaPrincipal: "ordem_servico_cliente",
    labelReferenciaPrincipal: "OS do cliente",
    referenciaObrigatoriaNaNota: true,
    textoReferenciaNaNota:
      "Mencionar a OS ou referência operacional do cliente nos dados complementares.",
    politicaAgrupamentoNota: "avaliar_caso_a_caso",
    permiteMultiplosServicosNaMesmaNota: true,
    exigeDescricaoIndividualPorServico: true,
    exigeAnexoRelatorioTecnico: true,
    exigeAnexoPedidoCompra: false,
    exigeLancamentoPortal: false,
    portalNome: null,
    portalUrl: null,
    prazoEnvioAposEmissaoHoras: 48,
    observacoesEmissao:
      "Conferir com o solicitante se existe referência interna, OS ou identificação do vessel/case.",
    observacoesEnvio:
      "Enviar PDF/DANFSe ao solicitante e XML ao fiscal quando houver contato fiscal informado.",
    contatosEnvio: [
      {
        id: 1,
        tipo: "solicitante",
        nome: "Solicitante Googlemarine",
        email: "solicitante@googlemarine.com",
        telefone: null,
        canal: "email",
        documento: "danfse",
        observacao: "Enviar PDF/DANFSe ao solicitante.",
        status: "obrigatorio",
      },
      {
        id: 2,
        tipo: "fiscal_cliente",
        nome: "Fiscal Googlemarine",
        email: "fiscal@googlemarine.com",
        telefone: null,
        canal: "email",
        documento: "xml",
        observacao: "Enviar XML quando solicitado pelo fiscal.",
        status: "opcional",
      },
    ],
    fluxo: [
      {
        id: 1,
        ordem: 1,
        titulo: "Conferir referência operacional",
        descricao:
          "Validar se o cliente possui OS, vessel, case ou referência interna para mencionar na nota.",
        obrigatoria: true,
      },
      {
        id: 2,
        ordem: 2,
        titulo: "Emitir nota",
        descricao:
          "Discriminar serviço e mencionar referência operacional nos complementares.",
        obrigatoria: true,
      },
      {
        id: 3,
        ordem: 3,
        titulo: "Enviar ao cliente",
        descricao:
          "Enviar DANFSe ao solicitante e XML ao fiscal quando aplicável.",
        obrigatoria: true,
      },
    ],
  },
  {
    id: 4,
    clienteId: 104,
    cliente: "OSM Thome Brasil",
    cnpjCpfCliente: "55.555.555/0001-55",
    grupoCliente: "OSM Thome",
    statusRegra: "pendente_revisao",
    statusValidacao: "desatualizada",
    responsavelInterno: "Financeiro",
    revisadoEm: "2026-03-12",
    proximaRevisao: "2026-05-12",
    exigeScAntesEmissao: false,
    exigePiPoAntesEmissao: true,
    tipoReferenciaPrincipal: "pedido_compra",
    labelReferenciaPrincipal: "Pedido de compra",
    referenciaObrigatoriaNaNota: true,
    textoReferenciaNaNota:
      "Mencionar o pedido de compra nos dados complementares.",
    politicaAgrupamentoNota: "uma_nota_por_pi_po",
    permiteMultiplosServicosNaMesmaNota: true,
    exigeDescricaoIndividualPorServico: true,
    exigeAnexoRelatorioTecnico: false,
    exigeAnexoPedidoCompra: true,
    exigeLancamentoPortal: true,
    portalNome: "Portal OSM Thome",
    portalUrl: "https://portal.osmthome.com",
    prazoEnvioAposEmissaoHoras: 24,
    observacoesEmissao:
      "Confirmar se o pedido de compra continua obrigatório para todas as emissões.",
    observacoesEnvio:
      "Procedimento precisa ser revisado antes da próxima emissão.",
    contatosEnvio: [
      {
        id: 1,
        tipo: "fiscal_cliente",
        nome: "Fiscal OSM Thome",
        email: "fiscal@osmthome.com",
        telefone: null,
        canal: "email",
        documento: "pdf_xml",
        observacao: "Contato ainda precisa ser confirmado.",
        status: "obrigatorio",
      },
    ],
    fluxo: [
      {
        id: 1,
        ordem: 1,
        titulo: "Confirmar pedido de compra",
        descricao:
          "Validar com o cliente se o pedido de compra é obrigatório antes da emissão.",
        obrigatoria: true,
      },
      {
        id: 2,
        ordem: 2,
        titulo: "Emitir NFS-e",
        descricao:
          "Mencionar pedido de compra nos dados complementares da nota.",
        obrigatoria: true,
      },
      {
        id: 3,
        ordem: 3,
        titulo: "Enviar documentos",
        descricao:
          "Enviar PDF/XML e verificar necessidade de lançamento no portal.",
        obrigatoria: true,
      },
    ],
  },
  {
    id: 5,
    clienteId: 105,
    cliente: "Vision Marine Electronics",
    cnpjCpfCliente: "Não informado",
    grupoCliente: "Vision Marine",
    statusRegra: "incompleta",
    statusValidacao: "sem_validacao",
    responsavelInterno: "Comercial",
    revisadoEm: null,
    proximaRevisao: null,
    exigeScAntesEmissao: false,
    exigePiPoAntesEmissao: false,
    tipoReferenciaPrincipal: "nao_exige",
    labelReferenciaPrincipal: "Não definido",
    referenciaObrigatoriaNaNota: false,
    textoReferenciaNaNota: "Procedimento ainda não definido.",
    politicaAgrupamentoNota: "avaliar_caso_a_caso",
    permiteMultiplosServicosNaMesmaNota: false,
    exigeDescricaoIndividualPorServico: true,
    exigeAnexoRelatorioTecnico: false,
    exigeAnexoPedidoCompra: false,
    exigeLancamentoPortal: false,
    portalNome: null,
    portalUrl: null,
    prazoEnvioAposEmissaoHoras: 48,
    observacoesEmissao:
      "Cliente ainda sem regra fiscal validada. Confirmar dados antes da primeira emissão.",
    observacoesEnvio:
      "Cadastrar e-mail fiscal e solicitante antes de emitir a primeira nota.",
    contatosEnvio: [],
    fluxo: [
      {
        id: 1,
        ordem: 1,
        titulo: "Completar cadastro",
        descricao:
          "Solicitar CNPJ, e-mail fiscal, solicitante e regra de emissão.",
        obrigatoria: true,
      },
    ],
  },
];

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [47, 63, 89];

  const clean = hex.replace("#", "").trim();

  if (clean.length !== 6) return [47, 63, 89];

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [47, 63, 89];
  }

  return [r, g, b];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function formatDate(value: string | null) {
  if (!value) return "Não informado";

  const [year, month, day] = value.split("-");

  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
}

function plural(value: number, singular: string, pluralText: string) {
  return value === 1 ? singular : pluralText;
}

function getSectionTitle(section: SectionKey) {
  switch (section) {
    case "com-pendencias":
      return "Com pendências";
    case "regras-ativas":
      return "Regras ativas";
    case "aguardando-revisao":
      return "Aguardando revisão";
    case "sem-regra":
      return "Sem regra";
    case "visao-geral":
    default:
      return "Visão geral";
  }
}

function getTipoContatoLabel(tipo: TipoContatoFiscal) {
  switch (tipo) {
    case "fiscal_cliente":
      return "Fiscal do cliente";
    case "solicitante":
      return "Solicitante";
    case "compras_cliente":
      return "Compras";
    case "financeiro_cliente":
      return "Financeiro do cliente";
    case "portal_cliente":
      return "Portal do cliente";
    case "outro":
      return "Outro";
    default:
      return "Contato";
  }
}

function getCanalLabel(canal: CanalEnvioCliente) {
  switch (canal) {
    case "email":
      return "E-mail";
    case "portal":
      return "Portal";
    case "whatsapp":
      return "WhatsApp";
    case "outro":
      return "Outro";
    default:
      return "Canal";
  }
}

function getDocumentoEnvioLabel(documento: DocumentoExigidoEnvio) {
  switch (documento) {
    case "pdf":
      return "PDF";
    case "xml":
      return "XML";
    case "pdf_xml":
      return "PDF + XML";
    case "danfse":
      return "DANFSe";
    case "outro":
      return "Outro";
    default:
      return "Documento";
  }
}

function getStatusItemRegraLabel(status: StatusItemRegra) {
  switch (status) {
    case "obrigatorio":
      return "Obrigatório";
    case "opcional":
      return "Opcional";
    case "nao_aplicavel":
      return "Não aplicável";
    default:
      return "Indefinido";
  }
}

function getStatusItemVisual(status: StatusItemRegra) {
  switch (status) {
    case "obrigatorio":
      return {
        background: "#eff6ff",
        color: "#1d4ed8",
        border: "#bfdbfe",
      };

    case "opcional":
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
      };

    case "nao_aplicavel":
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
      };
  }
}

function getPendenciasRegra(regra: RegraFiscalCliente) {
  const pendencias: string[] = [];

  if (regra.statusRegra === "incompleta" || regra.statusRegra === "sem_regra") {
    pendencias.push("Regra fiscal incompleta");
  }

  if (
    regra.statusValidacao === "desatualizada" ||
    regra.statusValidacao === "sem_validacao"
  ) {
    pendencias.push("Validação pendente");
  }

  if (regra.contatosEnvio.length === 0) {
    pendencias.push("Nenhum contato de envio cadastrado");
  }

  if (
    regra.referenciaObrigatoriaNaNota &&
    regra.tipoReferenciaPrincipal === "nao_exige"
  ) {
    pendencias.push("Referência obrigatória não definida");
  }

  if (regra.exigeLancamentoPortal && !regra.portalNome) {
    pendencias.push("Portal obrigatório não cadastrado");
  }

  return pendencias;
}

const RegraClienteRow: React.FC<{
  regra: RegraFiscalCliente;
  departmentColor: string;
  onOpen: (regra: RegraFiscalCliente) => void;
}> = ({ regra, departmentColor, onOpen }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <tr
      style={{
        ...styles.tr,
        background: hovered ? toRgba(departmentColor, 0.05) : "#ffffff",
        boxShadow: hovered
          ? `inset 3px 0 0 ${departmentColor}`
          : "inset 0 0 0 transparent",
      }}
      title="Duplo clique para abrir as orientações do cliente"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={() => onOpen(regra)}
    >
      <td style={styles.tdWide}>
        <div style={styles.clientCell}>
          <div
            style={{
              ...styles.clientIcon,
              color: hovered ? departmentColor : "#475569",
              borderColor: hovered ? toRgba(departmentColor, 0.3) : "#dbe4ef",
              background: hovered ? toRgba(departmentColor, 0.07) : "#f8fafc",
            }}
          >
            <Building2 size={15} />
          </div>

          <div style={styles.clientInfo}>
            <strong
              style={{
                ...styles.clientName,
                color: hovered ? departmentColor : "#111827",
              }}
            >
              {regra.cliente}
            </strong>

            <span style={styles.mutedText}>{regra.cnpjCpfCliente}</span>
          </div>
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.documentMini}>
          <span>{regra.exigeScAntesEmissao ? "Exige SC" : "Sem SC"}</span>
          <span>
            {regra.exigePiPoAntesEmissao ? "Exige PI/PO" : "Sem PI/PO"}
          </span>
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.documentMini}>
          <span>{regra.contatosEnvio.length} contato(s)</span>
          <span>
            {regra.exigeLancamentoPortal
              ? regra.portalNome || "Portal obrigatório"
              : "Sem portal"}
          </span>
        </div>
      </td>

      <td style={styles.tdNoRightBorder}>
        <div style={styles.mainText}>{formatDate(regra.revisadoEm)}</div>
        <div style={styles.mutedText}>
          Próxima {formatDate(regra.proximaRevisao)}
        </div>
      </td>
    </tr>
  );
};

const RegraClienteDetailOverlay: React.FC<{
  regra: RegraFiscalCliente | null;
  departmentColor: string;
  onClose: () => void;
}> = ({ regra, departmentColor, onClose }) => {
  if (!regra) return null;

  return (
    <div style={styles.overlayBackdrop}>
      <div style={styles.overlayPanel}>
        <div style={styles.overlayHeader}>
          <div>
            <div style={styles.overlayEyebrow}>
              Financeiro / Orientação fiscal por cliente
            </div>

            <h2 style={styles.overlayTitle}>{regra.cliente}</h2>

            <p style={styles.overlaySubtitle}>
              Consulta rápida do cliente vinculado, fluxo recomendado e contatos
              para envio da nota.
            </p>
          </div>

          <button
            type="button"
            style={styles.overlayCloseButton}
            onClick={onClose}
            aria-label="Fechar detalhamento"
          >
            <X size={17} />
          </button>
        </div>

        <div style={styles.detailContent}>
          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <Building2 size={15} />
              <strong>Cliente vinculado</strong>
            </div>

            <div style={styles.clientDetailGrid}>
              <div style={styles.infoItem}>
                <span>Cliente</span>
                <strong>{regra.cliente}</strong>
              </div>

              <div style={styles.infoItem}>
                <span>CNPJ / CPF</span>
                <strong>{regra.cnpjCpfCliente}</strong>
              </div>

              <div style={styles.infoItem}>
                <span>Grupo</span>
                <strong>{regra.grupoCliente}</strong>
              </div>
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <ClipboardCheck size={15} />
              <strong>Fluxo recomendado</strong>
            </div>

            <div style={styles.flowList}>
              {regra.fluxo.map((etapa) => (
                <div key={etapa.id} style={styles.flowItem}>
                  <div
                    style={{
                      ...styles.flowNumber,
                      background: toRgba(departmentColor, 0.1),
                      borderColor: toRgba(departmentColor, 0.25),
                      color: departmentColor,
                    }}
                  >
                    {etapa.ordem}
                  </div>

                  <div style={styles.flowContent}>
                    <div style={styles.flowTitleLine}>
                      <strong>{etapa.titulo}</strong>

                      {etapa.obrigatoria && (
                        <span style={styles.requiredText}>Obrigatório</span>
                      )}
                    </div>

                    <span>{etapa.descricao}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <MailCheck size={15} />
              <strong>Contatos para envio</strong>
            </div>

            <div style={styles.envioIntro}>
              <strong>Orientação de envio</strong>
              <span>{regra.observacoesEnvio}</span>
            </div>

            <div style={styles.contactFlowList}>
              {regra.contatosEnvio.length === 0 && (
                <div style={styles.emptyContacts}>
                  Nenhum contato de envio cadastrado para este cliente.
                </div>
              )}

              {regra.contatosEnvio.map((contato, index) => {
                const visual = getStatusItemVisual(contato.status);

                return (
                  <div key={contato.id} style={styles.contactFlowItem}>
                    <div
                      style={{
                        ...styles.contactFlowNumber,
                        background: toRgba(departmentColor, 0.08),
                        borderColor: toRgba(departmentColor, 0.22),
                        color: departmentColor,
                      }}
                    >
                      {index + 1}
                    </div>

                    <div style={styles.contactFlowContent}>
                      <div style={styles.contactFlowHeader}>
                        <div style={styles.contactFlowTitleArea}>
                          <strong style={styles.contactFlowTitle}>
                            {getTipoContatoLabel(contato.tipo)}
                          </strong>

                          <span style={styles.contactFlowSubtitle}>
                            {contato.nome}
                          </span>
                        </div>

                        <span
                          style={{
                            ...styles.simpleStatusBadge,
                            background: visual.background,
                            color: visual.color,
                            borderColor: visual.border,
                          }}
                        >
                          {getStatusItemRegraLabel(contato.status)}
                        </span>
                      </div>

                      <div style={styles.contactFlowMeta}>
                        <div style={styles.contactMetaItem}>
                          <span style={styles.contactMetaLabel}>
                            Documento
                          </span>

                          <strong style={styles.contactMetaValue}>
                            {getDocumentoEnvioLabel(contato.documento)}
                          </strong>
                        </div>

                        <div style={styles.contactMetaItem}>
                          <span style={styles.contactMetaLabel}>Canal</span>

                          <strong style={styles.contactMetaValue}>
                            {getCanalLabel(contato.canal)}
                          </strong>
                        </div>

                        <div style={styles.contactMetaItem}>
                          <span style={styles.contactMetaLabel}>E-mail</span>

                          <strong
                            style={{
                              ...styles.contactMetaValue,
                              color: contato.email ? "#1d4ed8" : "#64748b",
                            }}
                          >
                            {contato.email || "Não informado"}
                          </strong>
                        </div>

                        <div style={styles.contactMetaItem}>
                          <span style={styles.contactMetaLabel}>Telefone</span>

                          <strong style={styles.contactMetaValue}>
                            {contato.telefone || "Não informado"}
                          </strong>
                        </div>
                      </div>

                      <div style={styles.contactFlowObservation}>
                        {contato.observacao}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        <div style={styles.overlayFooter}>
          <button type="button" style={styles.cancelButton} onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

const RegrasFiscaisClientesLayout: React.FC<
  RegrasFiscaisClientesLayoutProps
> = ({ section }) => {
  const { departmentColor } = useTheme();

  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;
  const activeSection = section || "visao-geral";

  const [selectedRegra, setSelectedRegra] = useState<RegraFiscalCliente | null>(
    null
  );

  const filteredItems = useMemo(() => {
    switch (activeSection) {
      case "com-pendencias":
        return fixedRegras.filter(
          (regra) => getPendenciasRegra(regra).length > 0
        );

      case "regras-ativas":
        return fixedRegras.filter((regra) => regra.statusRegra === "ativa");

      case "aguardando-revisao":
        return fixedRegras.filter(
          (regra) =>
            regra.statusRegra === "pendente_revisao" ||
            regra.statusValidacao === "desatualizada" ||
            regra.statusValidacao === "pendente_validacao"
        );

      case "sem-regra":
        return fixedRegras.filter(
          (regra) =>
            regra.statusRegra === "sem_regra" ||
            regra.statusRegra === "incompleta"
        );

      case "visao-geral":
      default:
        return fixedRegras;
    }
  }, [activeSection]);

  return (
    <div style={styles.page}>
      <TitleButtons
        fullBleedLine
        title={`Orientações fiscais por cliente - ${getSectionTitle(
          activeSection
        )}`}
      />

      <div style={styles.content}>
        <section style={styles.actionBar}>
          <div style={styles.actionBarInfo}>
            <strong>Base de orientação para emissão e envio de notas</strong>
            <span>
              Defina por cliente o fluxo recomendado e os contatos corretos para
              envio de XML, DANFSe ou anexos.
            </span>
          </div>

          <div style={styles.actionButtons}>
            <button
              type="button"
              style={{
                ...styles.primaryButton,
                background: activeDepartmentColor,
                borderColor: activeDepartmentColor,
              }}
            >
              <Plus size={14} />
              Nova regra
            </button>

            <button type="button" style={styles.secondaryButton}>
              <Building2 size={14} />
              Vincular cliente
            </button>

            <button type="button" style={styles.secondaryButton}>
              <Mail size={14} />
              Cadastrar contato
            </button>

            <button type="button" style={styles.secondaryButton}>
              <FileText size={14} />
              Modelo de orientação
            </button>
          </div>
        </section>

        <section style={styles.tablePanel}>
          <div style={styles.tableHeader}>
            <div>
              <h2 style={styles.tableTitle}>Regras fiscais por cliente</h2>
              <span style={styles.tableHint}>
                {filteredItems.length}{" "}
                {plural(filteredItems.length, "registro", "registros")}{" "}
                encontrado(s). Duplo clique na linha abre a orientação resumida.
              </span>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.thWide}>Cliente</th>
                  <th style={styles.th}>Pré-emissão</th>
                  <th style={styles.th}>Envio / Portal</th>
                  <th style={styles.thNoRightBorder}>Revisão</th>
                </tr>
              </thead>

              <tbody>
                {filteredItems.map((regra) => (
                  <RegraClienteRow
                    key={regra.id}
                    regra={regra}
                    departmentColor={activeDepartmentColor}
                    onOpen={setSelectedRegra}
                  />
                ))}

                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={4} style={styles.emptyCell}>
                      Nenhuma regra fiscal encontrada para esta seção.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <RegraClienteDetailOverlay
        regra={selectedRegra}
        departmentColor={activeDepartmentColor}
        onClose={() => setSelectedRegra(null)}
      />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    width: "100%",
    color: "#111827",
    fontFamily:
      '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  content: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  actionBar: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    padding: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },

  actionBarInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 260,
    color: "#334155",
    fontSize: 12.4,
    fontWeight: 700,
  },

  actionButtons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 7,
    flexWrap: "wrap",
  },

  primaryButton: {
    height: 32,
    border: "1px solid",
    color: "#ffffff",
    padding: "0 12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  secondaryButton: {
    height: 32,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "0 11px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  tablePanel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    overflow: "hidden",
  },

  tableHeader: {
    minHeight: 44,
    padding: "8px 10px",
    borderBottom: "1px solid #dfe3e8",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  tableTitle: {
    margin: 0,
    fontSize: 13.5,
    fontWeight: 950,
    color: "#111827",
  },

  tableHint: {
    display: "block",
    marginTop: 1,
    fontSize: 11.2,
    fontWeight: 700,
    color: "#64748b",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    minWidth: 860,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  thWide: {
    width: "44%",
    padding: "8px 10px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: "1px solid #e6e9ed",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 950,
    color: "#3f4752",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  th: {
    padding: "8px 10px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: "1px solid #e6e9ed",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 950,
    color: "#3f4752",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  thNoRightBorder: {
    padding: "8px 10px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: 0,
    textAlign: "left",
    fontSize: 11,
    fontWeight: 950,
    color: "#3f4752",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  tr: {
    borderBottom: "1px solid #edf0f2",
    cursor: "pointer",
    background: "#ffffff",
    transition: "background 140ms ease, box-shadow 140ms ease",
  },

  tdWide: {
    padding: "8px 10px",
    fontSize: 12.2,
    fontWeight: 650,
    color: "#334155",
    verticalAlign: "middle",
    borderRight: "1px solid #f0f2f4",
    overflow: "hidden",
  },

  td: {
    padding: "8px 10px",
    fontSize: 12.2,
    fontWeight: 650,
    color: "#334155",
    verticalAlign: "middle",
    borderRight: "1px solid #f0f2f4",
    overflow: "hidden",
  },

  tdNoRightBorder: {
    padding: "8px 10px",
    fontSize: 12.2,
    fontWeight: 650,
    color: "#334155",
    verticalAlign: "middle",
    borderRight: 0,
    overflow: "hidden",
  },

  clientCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },

  clientIcon: {
    width: 29,
    height: 29,
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition:
      "background 140ms ease, color 140ms ease, border-color 140ms ease",
  },

  clientInfo: {
    minWidth: 0,
  },

  clientName: {
    display: "block",
    fontSize: 12.8,
    fontWeight: 950,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  mainText: {
    fontSize: 12.4,
    fontWeight: 900,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  mutedText: {
    marginTop: 1,
    display: "block",
    fontSize: 10.8,
    fontWeight: 650,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  documentMini: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: 11.5,
    fontWeight: 850,
  },

  emptyCell: {
    padding: 26,
    textAlign: "center",
    color: "#64748b",
    fontSize: 13,
    fontWeight: 750,
  },

  overlayBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(15, 23, 42, 0.42)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
  },

  overlayPanel: {
    width: "min(900px, 100%)",
    maxHeight: "calc(100vh - 36px)",
    overflowY: "auto",
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
  },

  overlayHeader: {
    padding: "16px 18px 14px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    alignItems: "flex-start",
  },

  overlayEyebrow: {
    fontSize: 11,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.35,
  },

  overlayTitle: {
    margin: "4px 0 0",
    fontSize: 18,
    fontWeight: 950,
    color: "#111827",
    lineHeight: 1.2,
  },

  overlaySubtitle: {
    margin: "5px 0 0",
    maxWidth: 680,
    fontSize: 12.4,
    fontWeight: 700,
    color: "#64748b",
    lineHeight: 1.35,
  },

  overlayCloseButton: {
    width: 32,
    height: 32,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#475569",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },

  detailContent: {
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  detailSection: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
  },

  detailSectionHeader: {
    minHeight: 38,
    padding: "9px 10px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    gap: 7,
    color: "#334155",
    fontSize: 12.6,
    fontWeight: 950,
  },

  clientDetailGrid: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "1.5fr 1fr 1fr",
    gap: 8,
  },

  infoItem: {
    minHeight: 58,
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    padding: "9px 10px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 3,
    minWidth: 0,
  },

  flowList: {
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  flowItem: {
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    padding: 9,
    display: "flex",
    gap: 9,
    alignItems: "flex-start",
  },

  flowNumber: {
    width: 28,
    height: 28,
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 950,
    flexShrink: 0,
  },

  flowContent: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    color: "#475569",
    fontSize: 12.3,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  flowTitleLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    color: "#111827",
    fontSize: 12.7,
    fontWeight: 950,
  },

  requiredText: {
    height: 20,
    padding: "0 7px",
    border: "1px solid #bfdbfe",
    background: "#eff6ff",
    color: "#1d4ed8",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 10.5,
    fontWeight: 900,
  },

  envioIntro: {
    margin: 10,
    marginBottom: 0,
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    padding: "9px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    color: "#475569",
    fontSize: 12.2,
    fontWeight: 750,
    lineHeight: 1.35,
  },

  contactFlowList: {
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  contactFlowItem: {
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    padding: 10,
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
  },

  contactFlowNumber: {
    width: 30,
    height: 30,
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 950,
    flexShrink: 0,
  },

  contactFlowContent: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  contactFlowHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
  },

  contactFlowTitleArea: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  },

  contactFlowTitle: {
    fontSize: 13,
    fontWeight: 950,
    color: "#111827",
    lineHeight: 1.2,
  },

  contactFlowSubtitle: {
    fontSize: 11.6,
    fontWeight: 700,
    color: "#64748b",
    lineHeight: 1.2,
  },

  simpleStatusBadge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10.8,
    fontWeight: 900,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  contactFlowMeta: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },

  contactMetaItem: {
    border: "1px solid #edf0f2",
    background: "#f8fafc",
    padding: "8px 9px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  },

  contactMetaLabel: {
    fontSize: 10.5,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    lineHeight: 1.15,
  },

  contactMetaValue: {
    fontSize: 12.2,
    fontWeight: 850,
    color: "#111827",
    lineHeight: 1.25,
    wordBreak: "break-word",
  },

  contactFlowObservation: {
    borderTop: "1px solid #edf0f2",
    paddingTop: 8,
    color: "#475569",
    fontSize: 12.2,
    fontWeight: 700,
    lineHeight: 1.45,
  },

  emptyContacts: {
    minHeight: 70,
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    fontSize: 12.4,
    fontWeight: 800,
  },

  overlayFooter: {
    padding: "10px 12px",
    borderTop: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
  },

  cancelButton: {
    height: 32,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#475569",
    padding: "0 12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },
};

export default RegrasFiscaisClientesLayout;